import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { signInWithSso, signInWithSsoCode } from '../../hooks/useSsoCredentials';
import { handleKeycloakCallback, readKeycloakAuthorizationResponse } from '../../utils/keycloak-auth';
import {
  clearMetrcSsoRedirectState,
  readMetrcSsoRedirectState,
} from '../../utils/metrcSsoRedirectState';
import { useImportsWizard } from './ImportsWizardContext';
import { getSafeEndpointMessage } from '../../utils/requestErrors';

export default function MetrcSsoCallbackContainer() {
  const navigate = useNavigate();
  const { setMetrcAuthData, setMetrcFacilitiesSelected, setReturnTo, setWorkflow } = useImportsWizard();
  const ranRef = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode / effect re-runs completing the one-time auth code exchange twice.
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      const stashed = readMetrcSsoRedirectState();
      clearMetrcSsoRedirectState();

      if (!stashed) {
        showToastError('SSO sign-in session expired. Please try again.');
        navigate('/imports/metrc/auth', { replace: true });
        return;
      }

      setReturnTo(stashed.returnTo);
      setWorkflow(stashed.workflow);
      const failurePath = stashed.workflow === 'reauth' ? (stashed.returnTo || '/profile') : '/imports/metrc/auth';
      const redirectUri = `${window.location.origin}/imports/metrc/sso-callback`;

      try {
        if (stashed.ssoClientAuth === 'confidential') {
          // RID-3173: the API exchanges the code with RetailID's signed client assertion and checks the nonce.
          const authorization = readKeycloakAuthorizationResponse();
          if (!authorization) {
            showToastError('SSO sign-in was not completed.');
            navigate(failurePath, { replace: true });
            return;
          }

          await signInWithSsoCode({
            organizationId: Number(stashed.organizationId),
            marketId: stashed.marketId,
            code: authorization.code,
            codeVerifier: authorization.codeVerifier,
            redirectUri,
            nonce: authorization.nonce,
          });
        } else {
          const tokens = await handleKeycloakCallback({
            ssoBaseUrl: stashed.ssoBaseUrl,
            clientId: stashed.ssoClientId,
            redirectUri,
          });

          if (!tokens?.id_token || !tokens.access_token || !tokens.refresh_token) {
            showToastError('SSO sign-in was not completed.');
            navigate(failurePath, { replace: true });
            return;
          }

          // The server verifies the sign-in with MCA before saving anything, so a rejection surfaces here.
          await signInWithSso({
            organizationId: Number(stashed.organizationId),
            marketId: stashed.marketId,
            ssoIdToken: tokens.id_token,
            ssoAccessToken: tokens.access_token,
            ssoRefreshToken: tokens.refresh_token,
          });
        }

        if (stashed.workflow === 'reauth') {
          showToastSuccess('Signed in to Metrc. You can try again now.');
          navigate(stashed.returnTo || '/profile', { replace: true });
          return;
        }

        setMetrcAuthData({ market: stashed.market, marketId: stashed.marketId });
        setMetrcFacilitiesSelected([]);
        navigate('/imports/metrc/select', { replace: true });
      } catch (error) {
        console.error(error);
        showToastError(getSafeEndpointMessage(error, 'Error completing SSO sign-in'));
        navigate(failurePath, { replace: true });
      }
    })();
  }, [navigate, setMetrcAuthData, setMetrcFacilitiesSelected, setReturnTo, setWorkflow]);

  return (
    <div className="card" style={{ padding: 16, margin: 24 }}>
      Completing sign-in…
    </div>
  );
}
